import { useState, useEffect, Fragment } from "react";
import { FormattedMessage as FM } from "react-intl";
import { useNavigate } from "react-router";
import { signal } from "@preact/signals-react";

import StdButton from "components/Buttons/StdButton";
import LabelRowSection from "./LabelRowSection";
import FolioCard from "./FolioCard";
import TitleSection from "./components/TitleSection";
import TitleHero from "../WebTra/components/ui/TitleHero";
import proxy from "api/proxy";
import PhotoPeople from "assets/apps/photo-people.jpg";
import {
  BedIcon,
  ExperiencesIcon,
  CustomerServicesIcon,
} from "components/Icons";
import Experiences from "./components/Experiences";
import { cn } from "tools/ui";
import imgMeal from "assets/apps/logo-melhous-yellow.png";
import { ICON_CHANNEL } from "../constant/constant.jsx";
import Dialog from "componentsv2/modal/Dialog";
import FormGuest from "./AddGuestPage";

const viewTitleExperiences = signal(true);
const openDialog = signal(false);
const currentFolio = signal(null);
const configHotel = signal(null);

function BookingPage({ booking }) {
  const { party } = booking;

  const [_, setConfirmDisabled] = useState(true);
  const navigate = useNavigate();

  const guestName = party?.name?.trim().split(" ")[0];

  async function getContract() {
    const { data: config } = await proxy.search(
      "hotel.configuration",
      [],
      ["registration_card_contract", "company.logo_link"],
    );

    if (config?.length) {
      configHotel.value = config[0];
    }
  }

  function getFinish() {
    const foliosWithGuests = [];
    for (const res of booking.lines) {
      if (res) {
        const folio = res[1];
        foliosWithGuests.push(folio.guests.length);
      }
    }
    const active = foliosWithGuests.every((val) => val > 0);
    setConfirmDisabled(!active);
  }

  useEffect(() => {
    getFinish();
    getContract();
  }, [booking]);

  const folios = [];
  booking.lines.forEach((folio) => {
    const validatePax =
      Number(folio.room.type_.max_accommodation) === folio.guests.size
        ? false
        : true;
    if (!folio.check_in_time) {
      folios.push(
        <FolioCard
          key={folio.id}
          openDialog={openDialog}
          currentFolio={currentFolio}
          folio={folio}
          mealPlan={booking?.meal_plan?.name}
          validatePax={validatePax}
        />,
      );
    }
  });

  async function finish() {
    navigate("success");
  }

  const channel = booking.channel.name;
  const iconChannel = ICON_CHANNEL[channel.toLowerCase() || "house"];

  return (
    <Fragment>
      <div className="max-w-[900px] mx-auto max-md:px-4">
        <TitleHero
          name={guestName}
          desc="app.webcheckin.intro_info"
          style="!max-w-[737px]"
          logo={configHotel.value?.["company."]?.logo_link}
        />

        <div className="bg-white rounded-2xl shadow-md">
          <section className="md:px-[39px] px-4">
            <h1 className="font-bold text-2xl md:text-[35px] capitalize py-3 text-cyan-600 md:text-start text-center ">
              <FM id="app.webcheckin.title" />
            </h1>

            <TitleSection title="app.webcheckin.reservation_detail">
              <CustomerServicesIcon className="size-[24px] fill-current text-neutral-900" />
            </TitleSection>

            <div className="md:flex md:space-x-[71px] space-y-3 ">
              {booking?.party?.name && (
                <LabelRowSection
                  label="app.webcheckin.customer"
                  data={booking.party.name}
                />
              )}

              {booking?.contact && (
                <LabelRowSection
                  label="app.webcheckin.contact"
                  data={booking.contact}
                />
              )}
              <div className="md:flex md:space-x-[71px] max-md:grid grid-cols-2 md:flex-shrink-0">
                <LabelRowSection
                  label="app.webcheckin.no_booking"
                  data={booking.number}
                />
                <LabelRowSection label="app.webcheckin.channel" data={channel}>
                  <img
                    src={iconChannel}
                    alt={channel}
                    className="size-10  mx-3"
                  />
                </LabelRowSection>
              </div>
            </div>

            <hr className="border-[#BFC2C8] my-[25px]" />

            <TitleSection title="app.webcheckin.reserved rooms">
              <BedIcon className="size-[24px] fill-current" />
            </TitleSection>

            <div className="md:grid md:grid-cols-2 gap-x-[16px] gap-y-[12px]">
              {folios}
            </div>

            <hr className="border-[#BFC2C8] my-[25px]" />
          </section>

          {booking?.link_melhous && (
            <section
              className={cn(
                "md:px-[39px] px-2 md:mt-2",
                viewTitleExperiences.value && "mb-10",
              )}
            >
              <TitleSection title="app.webcheckin.experiences.melhous" />

              <button
                type="button"
                onClick={() => window.open(booking?.link_melhous, "_blank")}
                className="relative flex w-full md:h-[200px] overflow-hidden pl-4 bg-neutral-950 rounded-[30px]"
              >
                <div className="flex flex-col md:items-start justify-center md:px-5 md:w-[85%] w-full items-center mb-8">
                  <img
                    src={imgMeal}
                    className="md:h-25 h-20  md:ml-4"
                    alt="logo-melhous-yellow-y"
                  />
                  <span className="text-gray-100 md:text-base text-xs text-start ml-4">
                    <FM id="app.webcheckin.melhos.description" />
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(booking?.link_melhous, "_blank");
                      }}
                      className="text-yellow-400 underline underline-offset-2 cursor-pointer hover:text-yellow-300 "
                    >
                      <FM id="app.webcheckin.melhos.click" />
                    </span>
                  </span>
                </div>

                <div className="relative w-50 md:w-1/2 md:h-full ">
                  <img
                    src={PhotoPeople}
                    alt=""
                    className="h-full w-full object-cover "
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/70   via-black/55 to-black" />
                </div>
              </button>
            </section>
          )}

          {viewTitleExperiences.value && (
            <section className="mt-10 md:pl-[39px] pl-1">
              <TitleSection title="app.webcheckin.experiences">
                <ExperiencesIcon className="size-[24px] fill-current ml-5 md:ml-0" />
              </TitleSection>

              <div className="mb-4 md:text-lg  text-sm text-start ">
                <FM id="app.webcheckin.experiences.description" />
              </div>

              <Experiences
                transparentLayer
                handleRecords={(value) => {
                  if (value === 0) {
                    viewTitleExperiences.value = false;
                  }
                }}
              />
            </section>
          )}
          <section className="px-4 py-4 ">
            <StdButton
              content="app.webcheckin.finish"
              size="w-full"
              color="black"
              onClick={finish}
            />
          </section>
        </div>
      </div>
      <Dialog
        title="app.webcheckin.register_guest"
        size="md:w-[1120px]"
        openModal={openDialog}
        handleClose={() => (openDialog.value = false)}
      >
        <FormGuest currentFolio={currentFolio} />
      </Dialog>
    </Fragment>
  );
}

export default BookingPage;
